import React, { FormEvent, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import { toast } from '../../ui/toast';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { enrollBegin, enrollConfirm } from '../../utils/api_service/mfa_api';
import { hasSession, storeSession } from '../../utils/auth/session';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { clearMfaGate } from '../../utils/auth/mfaGate';
import { clearPending, takeToken, urlCarriesToken } from '../../utils/auth/mfaHandoff';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';

type Stage = 'loading' | 'setup' | 'codes';

/**
 * Required two-factor enrolment. Reached three ways, all of which end in a
 * session that records the second factor:
 *  - password login returned mfaEnrollmentRequired (token in sessionStorage);
 *  - an SSO login was redirected here by the backend (?token=&next=);
 *  - an already signed-in user hit the in-app gate because the tenant now
 *    requires MFA (no token: the session bearer identifies them).
 */
export const MfaEnroll = () => {
  const history = useHistory();
  const [{ token: enrollToken, next }] = useState(() => takeToken('enroll'));
  const signedIn = hasSession();
  const [stage, setStage] = useState<Stage>('loading');
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [enrollmentId, setEnrollmentId] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!enrollToken && !signedIn) { history.push('/login'); return; }
    if (urlCarriesToken()) history.replace('/auth/mfa/enroll');
    enrollBegin(enrollToken ? { enrollToken } : {})
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) { toast.error('Could not start setup.', { position: 'bottom-center' }); return; }
        setSecret(data.secret);
        setOtpauthUri(data.otpauthUri || '');
        setEnrollmentId(data.enrollmentId);
        setStage('setup');
      })
      .catch(() => toast.error('Could not start setup.', { position: 'bottom-center' }));
    // eslint-disable-next-line
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    const res = await enrollConfirm({ enrollmentId, code, enrollToken: enrollToken || undefined });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast.error('That code was not accepted.', { position: 'bottom-center' });
      return;
    }
    setRecoveryCodes(data.recoveryCodes || []);
    // Both the forced (login) and in-app paths return a session that carries
    // the second factor; store it so the gate and the refresh backstop clear.
    if (data.accessToken) storeSession(data);
    setStage('codes');
  };

  const finish = async () => {
    clearPending('enroll');
    clearMfaGate();
    // Session was already stored on confirm; hydrate + route to the deep link.
    history.push(await finishSignIn(undefined, { deepLink: next }));
  };

  if (!enrollToken && !signedIn) return null;

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <Stack spacing={2.5} sx={{ width: '100%', pt: 5 }}>
        <Alert severity='warning' sx={{ borderRadius: '12px', fontSize: '0.875rem' }}>
          <AlertTitle sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Two-factor authentication is required.</AlertTitle>
          Your organisation requires every employee account to use two-factor authentication. Set it up now to continue.
        </Alert>

        {stage === 'loading' && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Preparing two-factor setup…
          </Typography>
        )}

        {stage === 'setup' && (
          <Box component='form' onSubmit={confirm} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant='h5' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Set up two-factor authentication
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password), or
              enter the key manually, then type the 6-digit code it shows.
            </Typography>
            {otpauthUri && (
              <Paper
                variant='outlined'
                sx={{ alignSelf: 'flex-start', borderRadius: '16px', p: 2, bgcolor: '#ffffff' }}
              >
                <QRCodeSVG value={otpauthUri} size={168} />
              </Paper>
            )}
            <Paper
              variant='outlined'
              sx={{
                borderRadius: '12px',
                bgcolor: 'rgba(241, 245, 249, 0.8)',
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                letterSpacing: '0.1em',
                wordBreak: 'break-all',
              }}
            >
              {secret}
            </Paper>
            <TextInput
              type='text'
              name='code'
              label='Authentication code'
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
            />
            <Button type='submit' label='Verify and enable' />
          </Box>
        )}

        {stage === 'codes' && (
          <Stack spacing={2.5}>
            <Typography variant='h5' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Save your recovery codes
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              Each code can be used once if you lose your authenticator. Store them somewhere safe — they will not
              be shown again.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {recoveryCodes.map((c) => (
                <Paper
                  key={c}
                  variant='outlined'
                  sx={{
                    borderRadius: '8px',
                    bgcolor: 'rgba(241, 245, 249, 0.8)',
                    px: 1.5,
                    py: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    textAlign: 'center',
                  }}
                >
                  {c}
                </Paper>
              ))}
            </Box>
            <Button onClick={finish} label='I have saved my codes' />
          </Stack>
        )}
      </Stack>
    </AuthenticationUiLayout>
  );
};
